<template lang="pug">
div
  //- Card Details Section (compact inline version when visual is hidden)
  div(v-if="hideCardVisual" class="mb-3 gap-y-2")
    //- Main card info in one line (e.g., US Credit VISA **** 8592)
    .flex.items-center.gap-2
      svg(v-if="card.brand === 'visa'" class="w-6 h-4" viewBox="0 0 750 471" xmlns="http://www.w3.org/2000/svg")
        rect(width="750" height="471" rx="40" fill="#1A1F71")
        path(d="M278.2 334.2l33.4-195.8h53.3l-33.5 195.8h-53.2zm246.7-191.2c-10.6-4-27.2-8.3-48-8.3-52.9 0-90.2 26.7-90.5 65 0 28.3 26.6 44 46.9 53.4 20.8 9.6 27.8 15.8 27.7 24.4 0 13.2-16.6 19.2-32 19.2-21.4 0-32.8-3-50.4-10.3l-6.9-3.1-7.5 43.8c12.5 5.5 35.7 10.3 59.7 10.5 56.3 0 92.8-26.4 93.2-67.3 0-22.4-14-39.5-45-53.6-18.7-9.1-30.2-15.2-30-24.3 0-8.2 9.7-16.9 30.6-16.9 17.5 0 30.2 3.6 40.1 7.6l4.8 2.3 7.3-42.7zm138.3-4.3h-41.4c-12.8 0-22.4 3.5-28.1 16.3l-79.6 179.5h56.2s9.2-24.1 11.3-29.4h68.6c1.6 6.9 6.5 29.4 6.5 29.4h49.7l-43.2-195.8zm-66.4 126.5c4.4-11.3 21.3-54.8 21.3-54.8s4.4-11.4 7.2-18.8l3.7 17s10.2 46.5 12.4 56.6h-44.6zm-285.8-126.5l-52.5 133.3-5.6-27.4c-9.7-31.1-40.2-64.9-74.2-81.8l48 171.3h56.6l84.2-195.4h-56.5z" fill="white")
      svg(v-else-if="card.brand === 'mastercard'" class="w-6 h-4" viewBox="0 0 750 471" xmlns="http://www.w3.org/2000/svg")
        circle(cx="298" cy="235" r="147" fill="#FF5F00")
        circle(cx="452" cy="235" r="147" fill="#EB001B" opacity="0.8")
      svg(v-else-if="card.brand === 'amex'" class="w-6 h-4" viewBox="0 0 750 471" xmlns="http://www.w3.org/2000/svg")
        rect(width="750" height="471" rx="40" fill="#2E77BC")
        path(d="M0 220.3h37.9l8.5-20.5h19l8.5 20.5h74.2v-15.6l6.6 15.7h38.5l6.6-15.9v15.9h184.6l0-33.8 3.6-0.5c2.5 0 3.3 0.3 3.3 4.2v30h95.5v-8c7.7 4.1 19.6 8 35.3 8h40l8.6-20.5h19l8.5 20.5h77.2v-19.5l11.7 19.5h61.9V141h-61.3v15.3l-8.6-15.3h-62.9v15.3l-7.9-15.3H409.5c-14.2 0-26.7 2-36.7 7.5V141h-58.7v12.5c-6.4-9.5-18.5-12.5-30.1-12.5H100.7L84 182l-16.3-41H2.2v15.2L0 141zm227.2-60.8h-27.8" fill="white")
      svg(v-else-if="card.brand === 'discover'" class="w-6 h-4" viewBox="0 0 750 471" xmlns="http://www.w3.org/2000/svg")
        rect(width="750" height="471" rx="40" fill="#FF6000")
        path(d="M84 234c0-79.5 64.5-144 144-144s144 64.5 144 144-64.5 144-144 144-144-64.5-144-144z" fill="#FFF")
      svg(v-else class="w-5 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24")
        rect(x="2" y="5" width="20" height="14" rx="2")
        line(x1="2" y1="10" x2="22" y2="10")
      span.text-sm.font-medium {{ card.country || 'US' }} {{ cardFunding }} {{ card.brand ? card.brand.toUpperCase() : 'CARD' }} **** {{ card.last4 }} {{ cardExpiry !== '—' ? '(' + cardExpiry + ')' : '' }}
      a(v-if="card.fingerprint" :href="`https://dashboard.stripe.com/search?query=fingerprint:${card.fingerprint}`" target="_blank" class="ml-auto text-xs text-blue-600 hover:underline inline-flex items-center gap-1")
        | FP: {{ card.fingerprint.substring(0, 8) }}...
        Icon(name="lucide:external-link" class="w-2.5 h-2.5")
    //- Verification details in second line (show all checks)
    .flex.items-center.gap-3.text-xs
      //- 3D Secure
      div.flex.items-center.gap-1
        span.text-muted-foreground 3DS:
        span(:class="getThreeDSecureClass(threeDSecureStatus)") {{ threeDSecureDisplay || 'N/A' }}
      //- CVC Check
      div.flex.items-center.gap-1(v-if="cvcCheck || isStripePayment")
        span.text-muted-foreground CVC:
        span(:class="getCheckClass(cvcCheck)") {{ cvcCheck?.toUpperCase() || 'N/A' }}
      //- Address Line 1 Check
      div.flex.items-center.gap-1(v-if="addressLine1Check || isStripePayment")
        span.text-muted-foreground Addr:
        span(:class="getCheckClass(addressLine1Check)") {{ addressLine1Check?.toUpperCase() || 'N/A' }}
      //- Postal Code Check
      div.flex.items-center.gap-1(v-if="postalCodeCheck || isStripePayment")
        span.text-muted-foreground ZIP:
        span(:class="getCheckClass(postalCodeCheck)") {{ postalCodeCheck?.toUpperCase() || 'N/A' }}
      //- Any other checks from card.checks
      template(v-if="card.checks")
        div.flex.items-center.gap-1(v-if="card.checks.address_postal_code_check && !postalCodeCheck")
          span.text-muted-foreground Postal:
          span(:class="getCheckClass(card.checks.address_postal_code_check)") {{ card.checks.address_postal_code_check?.toUpperCase() }}
        div.flex.items-center.gap-1(v-if="card.checks.funding_check")
          span.text-muted-foreground Funding:
          span(:class="getCheckClass(card.checks.funding_check)") {{ card.checks.funding_check?.toUpperCase() }}
  
  //- Card Details Section (full visual)
  div(v-if="!hideCardVisual" class="mb-4")
    h3.text-sm.font-semibold.mb-3 Card Details
    //- Credit Card Display
    div(class="relative w-full max-w-sm mx-auto")
      div(class="relative h-48 rounded-xl shadow-xl p-6 flex flex-col justify-between" :class="cardBackgroundClass")
        //- Card Brand Logo
        div.flex.justify-between.items-start.mb-4
          div.text-white
            //- Card brand icons
            svg(v-if="card.brand === 'visa'" class="w-14 h-9" viewBox="0 0 750 471" xmlns="http://www.w3.org/2000/svg")
              path(d="M278.2 334.2l33.4-195.8h53.3l-33.5 195.8h-53.2zm246.7-191.2c-10.6-4-27.2-8.3-48-8.3-52.9 0-90.2 26.7-90.5 65 0 28.3 26.6 44 46.9 53.4 20.8 9.6 27.8 15.8 27.7 24.4 0 13.2-16.6 19.2-32 19.2-21.4 0-32.8-3-50.4-10.3l-6.9-3.1-7.5 43.8c12.5 5.5 35.7 10.3 59.7 10.5 56.3 0 92.8-26.4 93.2-67.3 0-22.4-14-39.5-45-53.6-18.7-9.1-30.2-15.2-30-24.3 0-8.2 9.7-16.9 30.6-16.9 17.5 0 30.2 3.6 40.1 7.6l4.8 2.3 7.3-42.7zm138.3-4.3h-41.4c-12.8 0-22.4 3.5-28.1 16.3l-79.6 179.5h56.2s9.2-24.1 11.3-29.4h68.6c1.6 6.9 6.5 29.4 6.5 29.4h49.7l-43.2-195.8zm-66.4 126.5c4.4-11.3 21.3-54.8 21.3-54.8s4.4-11.4 7.2-18.8l3.7 17s10.2 46.5 12.4 56.6h-44.6zm-285.8-126.5l-52.5 133.3-5.6-27.4c-9.7-31.1-40.2-64.9-74.2-81.8l48 171.3h56.6l84.2-195.4h-56.5z" fill="white")
            svg(v-else-if="card.brand === 'mastercard'" class="w-14 h-9" viewBox="0 0 750 471" xmlns="http://www.w3.org/2000/svg")
              circle(cx="298" cy="235" r="147" fill="#FF5F00")
              circle(cx="452" cy="235" r="147" fill="#EB001B" opacity="0.8")
            svg(v-else-if="card.brand === 'amex'" class="w-14 h-9" viewBox="0 0 750 471" xmlns="http://www.w3.org/2000/svg")
              path(d="M0 220.3h37.9l8.5-20.5h19l8.5 20.5h74.2v-15.6l6.6 15.7h38.5l6.6-15.9v15.9h184.6l0-33.8 3.6-0.5c2.5 0 3.3 0.3 3.3 4.2v30h95.5v-8c7.7 4.1 19.6 8 35.3 8h40l8.6-20.5h19l8.5 20.5h77.2v-19.5l11.7 19.5h61.9V141h-61.3v15.3l-8.6-15.3h-62.9v15.3l-7.9-15.3H409.5c-14.2 0-26.7 2-36.7 7.5V141h-58.7v12.5c-6.4-9.5-18.5-12.5-30.1-12.5H100.7L84 182l-16.3-41H2.2v15.2L0 141zm227.2-60.8h-27.8" fill="white")
            svg(v-else-if="card.brand === 'discover'" class="w-14 h-9" viewBox="0 0 750 471" xmlns="http://www.w3.org/2000/svg")
              path(d="M84 234c0-79.5 64.5-144 144-144s144 64.5 144 144-64.5 144-144 144-144-64.5-144-144z" fill="#FFF")
            svg(v-else class="w-10 h-8" fill="none" stroke="white" stroke-width="2" viewBox="0 0 24 24")
              rect(x="2" y="5" width="20" height="14" rx="2")
              line(x1="2" y1="10" x2="22" y2="10")
          //- Country display
          div(v-if="card.country" class="text-white text-sm font-medium")
            | {{ card.country }}
        //- Card Number
        div.text-white.font-mono.text-lg.tracking-wider
          | •••• •••• •••• {{ card.last4 || '••••' }}
        //- Card Holder and Expiry
        div.flex.justify-between.items-end
          div
            p.text-xs.text-white.opacity-70.uppercase CARD HOLDER
            p.text-sm.text-white.font-medium.uppercase {{ cardHolderName }}
          div.text-right
            p.text-xs.text-white.opacity-70.uppercase EXPIRES
            p.text-sm.text-white.font-medium {{ cardExpiry }}
    //- Additional Card Info
    .grid.grid-cols-3.gap-4.mt-4
      div
        p.text-xs.text-muted-foreground.mb-1 Funding
        p.text-sm {{ cardFunding }}
      div
        p.text-xs.text-muted-foreground.mb-1 Country
        p.text-sm {{ card.country || '—' }}
      div(v-if="card.address_zip")
        p.text-xs.text-muted-foreground.mb-1 ZIP
        p.text-sm.font-mono {{ card.address_zip }}
    //- Verification Checks
    div(v-if="hasVerificationChecks")
      p.text-xs.text-muted-foreground.mb-2 Verification Checks
      .grid.grid-cols-2.gap-2
        .flex.justify-between.text-xs(v-if="cvcCheck")
          span.text-muted-foreground CVC
          span(:class="getCheckClass(cvcCheck)") {{ cvcCheck.toUpperCase() }}
        .flex.justify-between.text-xs(v-if="addressLine1Check")
          span.text-muted-foreground Address Line 1
          span(:class="getCheckClass(addressLine1Check)") {{ addressLine1Check.toUpperCase() }}
        .flex.justify-between.text-xs(v-if="postalCodeCheck")
          span.text-muted-foreground Postal Code
          span(:class="getCheckClass(postalCodeCheck)") {{ postalCodeCheck.toUpperCase() }}
        .flex.justify-between.text-xs
          span.text-muted-foreground 3D Secure
          span(:class="getThreeDSecureClass(threeDSecureStatus)") {{ threeDSecureDisplay || 'Not Available' }}
    //- Fingerprint
    div(v-if="card.fingerprint" class="mt-2")
      p.text-xs.text-muted-foreground.mb-1 Fingerprint
      a(:href="`https://dashboard.stripe.com/search?query=fingerprint:${card.fingerprint}`" target="_blank" class="text-sm font-mono text-xs text-blue-600 hover:underline inline-flex items-center gap-1")
        | {{ card.fingerprint }}
        Icon(name="lucide:external-link" class="w-3 h-3")
</template>

<script lang="ts" setup>
import { computed } from 'vue'

const props = defineProps<{
  card: any
  hideCardVisual?: boolean
  isStripePayment?: boolean
  cardHolderName?: string
  data?: any
}>()

const cardExpiry = computed(() => {
  if (!props.card?.exp_month || !props.card?.exp_year) return '—'
  return `${String(props.card.exp_month).padStart(2, '0')}/${String(props.card.exp_year).slice(-2)}`
})

const cardFunding = computed(() => {
  if (!props.card?.funding) return '—'
  return props.card.funding.charAt(0).toUpperCase() + props.card.funding.slice(1)
})

const cardBackgroundClass = computed(() => {
  const brand = props.card?.brand?.toLowerCase()
  switch (brand) {
    case 'visa':
      return 'bg-gradient-to-br from-blue-600 to-blue-800'
    case 'mastercard':
      return 'bg-gradient-to-br from-red-500 to-yellow-600'
    case 'amex':
      return 'bg-gradient-to-br from-green-600 to-green-800'
    case 'discover':
      return 'bg-gradient-to-br from-orange-500 to-orange-700'
    default:
      return 'bg-gradient-to-br from-gray-700 to-gray-900'
  }
})

// Verification checks
const cvcCheck = computed(() => props.card?.checks?.cvc_check || props.card?.cvc_check)
const addressLine1Check = computed(() => props.card?.checks?.address_line1_check || props.card?.address_line1_check)
const postalCodeCheck = computed(() => 
  props.card?.checks?.address_postal_code_check || 
  props.card?.address_postal_code_check || 
  props.card?.address_zip_check
)

const threeDSecureStatus = computed(() => 
  props.card?.three_d_secure || 
  props.data?.extra?.three_d_secure?.status || 
  props.data?.extra?.three_d_secure ||
  props.card?.three_d_secure_usage?.supported
)

const threeDSecureDisplay = computed(() => {
  const status = props.card?.three_d_secure || 
    props.data?.extra?.three_d_secure?.status || 
    props.data?.extra?.three_d_secure
  const supported = props.card?.three_d_secure_usage?.supported
  const version = props.data?.extra?.three_d_secure?.version
  
  if (status) {
    const displayStatus = typeof status === 'string' ? status.toUpperCase() : 'ENABLED'
    return version ? `${displayStatus} (v${version})` : displayStatus
  }
  if (supported !== undefined) {
    return supported ? 'SUPPORTED' : 'NOT SUPPORTED'
  }
  return '—'
})

const hasVerificationChecks = computed(() => 
  cvcCheck.value || addressLine1Check.value || postalCodeCheck.value || threeDSecureStatus.value
)

function getCheckClass(check: string) {
  if (check === 'pass') return 'text-green-600 font-medium'
  if (check === 'fail') return 'text-red-600 font-medium'
  return ''
}

function getThreeDSecureClass(status: any) {
  if (status === 'succeeded' || status === 'authenticated' || status === true) {
    return 'text-green-600 font-medium'
  }
  if (status === 'failed' || status === 'rejected') {
    return 'text-red-600 font-medium'
  }
  if (status === 'processing' || status === 'required') {
    return 'text-yellow-600 font-medium'
  }
  return ''
}
</script>